<template>
  <BContainer class="container-pages" v-if="subcategory">
    <BRow>
      <BCol xl="8">
        <div class="shadow rounded p-element-page bg-white">
          <h1 class="h1-title">{{ subcategory.title }}</h1>
          <hr />
        </div>

        <div class="a-container mb-3" v-if="$screen.showMobileAds">
          <OtherMobile />
        </div>

        <div class="shadow rounded p-element-page bg-white mt-2">
          <div v-html="subcategory.description"></div>
        </div>

        <div class="a-container mb-3" v-if="$screen.isProduction">
          <OtherBottom />
        </div>
      </BCol>

      <BCol xl="4">
        <div class="a-container mb-3" v-if="$screen.showDesktopAds">
          <OtherTopRight />
        </div>

        <WidgetsAdvertisementTopAdsPages
          :items="item?.data"
          :category_name="''"
          :display_img="true"
        />
      </BCol>
    </BRow>
  </BContainer>
</template>

<script setup>
import { BContainer, BRow, BCol } from 'bootstrap-vue-next'
import { WidgetsAdvertisementTopAdsPages } from '#components'

import OtherMobile from '~/components/GoogleAdvertisements/OtherMobile.vue'
import OtherBottom from '~/components/GoogleAdvertisements/OtherBottom.vue'
import OtherTopRight from '~/components/GoogleAdvertisements/OtherTopRight.vue'

import protectSubcategory from '~/util/sickProtection/protectSubcategory'
import { useCustomerStore } from '~/stores/customer'
import { useShareMetaStore } from '~/stores/shareMeta'

const useStore = useCustomerStore()
const shareMeta = useShareMetaStore()

const config = useRuntimeConfig()
const route = useRoute()
const device = useDevice()

const isProdMode = computed(() => !!config.public.isProdMode)

// 1) subcategory kao computed (nema ručnog setovanja u ref)
const subcategory = computed(() => {
  const slug = String(route.params.type || '')
    .toLowerCase()
    .split('-')
    .join(' ')
    .trim()

  const found = protectSubcategory?.subcategory?.find(
    (x) => String(x.title_path || '').toLowerCase() === slug
  )

  return found || null
})

// 2) ads (top)
const { data: item } = await useAsyncData(
  'item-bolesti-det',
  () => useStore.getCacheTopAd()
)

// 3) SEO – sve preko computed-a + usePijaceSeo (SSR garantovano)
const canonicalUrl = computed(() => `https://pijace.com${route.fullPath}`)

const headTitle = computed(() => subcategory.value?.title ?? 'Zaštita od bolesti | Pijace.com')

const headDescription = computed(() => {
  const txt = subcategory.value?.description || ''
  const plain = String(txt).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  if (plain) return plain.length > 200 ? plain.slice(0, 197) + '...' : plain
  return `${subcategory.value?.title ?? 'Zaštita od bolesti'} — informacije, preparati i preporuke za odbranu od bolesti i štetočina.`
})

// JSON-LD: Article (sadržaj o specifičnom tipu zaštite — HTML opis u v-html)
const articleLd = computed(() => {
  const sub = subcategory.value
  if (!sub) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl.value}#article`,
    headline: sub.title,
    description: headDescription.value,
    url: canonicalUrl.value,
    inLanguage: 'sr',
    author: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://pijace.com/images/pijace-60.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl.value
    }
  }
})

// JSON-LD: BreadcrumbList — Pijace.com → Zaštita → (parent kategorija) → {tip}
const breadcrumbLd = computed(() => {
  const sub = subcategory.value
  if (!sub) return null

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Pijace.com',
      item: 'https://pijace.com'
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Zaštita',
      item: 'https://pijace.com/zastita'
    }
  ]

  let position = 3

  // Roditelj-kategorija (npr. "Zaštita voća") ako postoji
  if (sub.category) {
    const parentSlug = String(sub.category).toLowerCase().split(' ').join('-')
    items.push({
      '@type': 'ListItem',
      position: position++,
      name: sub.category,
      item: `https://pijace.com/zastita/${parentSlug}`
    })
  }

  items.push({
    '@type': 'ListItem',
    position: position,
    name: sub.title,
    item: canonicalUrl.value
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  }
})

watchEffect(() => {
  if (!subcategory.value) return
  shareMeta.setMeta({
    title: headTitle.value,
    description: headDescription.value,
    url: canonicalUrl.value,
  })
})

usePijaceSeo({
  title: headTitle,
  description: headDescription,
  url: canonicalUrl,
  type: 'article',
  jsonLd: () => [articleLd.value, breadcrumbLd.value],
})
</script>

<style></style>
